import { useEffect, useRef, useState } from "react";
import * as faceapi from "face-api.js";

export default function CandidateSecureTestPage() {
    const videoRef = useRef<HTMLVideoElement | null>(null);

    // State
    const [cameraOn, setCameraOn] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [faceDetected, setFaceDetected] = useState(false);
    const [headDirection, setHeadDirection] = useState("Center");
    const [logs, setLogs] = useState<string[]>([]);
    const [violations, setViolations] = useState(0);
    const [testEnded, setTestEnded] = useState(false);

    // ✅ Log helper
    function logEvent(msg: string) {
        setLogs((prev) => [...prev, `${new Date().toLocaleTimeString()} - ${msg}`]);
        console.log("📝 Log:", msg);
    }

    // ✅ Register violation (end test on 2nd)
    function registerViolation(reason: string) {
        setViolations((prev) => {
            const newCount = prev + 1;
            logEvent(`⚠️ Violation: ${reason} (count=${newCount})`);

            if (newCount >= 2) {
                logEvent("❌ Test ended due to repeated violations.");
                setTestEnded(true);
                // TODO: Auto-submit / log to Supabase here
            }
            return newCount;
        });
    }

    // ✅ Load face-api models
    useEffect(() => {
        async function loadModels() {
            await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
            await faceapi.nets.faceLandmark68TinyNet.loadFromUri("/models");
            logEvent("Face detection models loaded ✅");
        }
        loadModels();
    }, []);

    // ✅ Request camera
    async function enableCamera() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
            setCameraOn(true);
            logEvent("Camera enabled ✅");
        } catch (err) {
            logEvent("Camera denied ❌");
        }
    }

    // ✅ Detect face + head pose
    useEffect(() => {
        if (!cameraOn) return;

        const interval = setInterval(async () => {
            if (videoRef.current) {
                const detection = await faceapi
                    .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
                    .withFaceLandmarks(true);

                if (!detection) {
                    setFaceDetected(false);
                    logEvent("⚠️ No face detected");
                    return;
                }

                setFaceDetected(true);

                // Estimate head direction
                const nose = detection.landmarks.getNose()[3];
                const leftEye = detection.landmarks.getLeftEye()[0];
                const rightEye = detection.landmarks.getRightEye()[3];
                const eyeCenterX = (leftEye.x + rightEye.x) / 2;
                const offset = nose.x - eyeCenterX;

                let direction = "Center";
                if (offset > 20) direction = "Looking Right";
                if (offset < -20) direction = "Looking Left";

                setHeadDirection(direction);
                logEvent(`Face detected: ${direction}`);
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [cameraOn]);

    // ✅ Tab/window switching
    useEffect(() => {
        function handleBlur() {
            registerViolation("Window lost focus (possible monitor switch)");
        }
        window.addEventListener("blur", handleBlur);
        return () => window.removeEventListener("blur", handleBlur);
    }, []);

    // ✅ Fullscreen detection
    useEffect(() => {
        function handleFullscreen() {
            setFullscreen(!!document.fullscreenElement);
            if (!document.fullscreenElement) {
                registerViolation("Exited fullscreen");
            }
        }
        document.addEventListener("fullscreenchange", handleFullscreen);
        return () => document.removeEventListener("fullscreenchange", handleFullscreen);
    }, []);

    // ✅ Request fullscreen
    function requestFullscreen() {
        if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen();
        }
    }

    return (
        <div className="max-w-3xl mx-auto p-6 space-y-6">
            <h1 className="text-2xl font-bold">Secure Candidate Test (Prototype)</h1>

            {/* Controls */}
            <div className="space-x-3">
                <button
                    onClick={requestFullscreen}
                    className="px-4 py-2 bg-indigo-600 text-white rounded"
                >
                    Enter Fullscreen
                </button>
                <button
                    onClick={enableCamera}
                    className="px-4 py-2 bg-green-600 text-white rounded"
                >
                    Enable Camera
                </button>
            </div>

            {/* Camera preview */}
            <div className="mt-4">
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className={`w-64 h-48 border ${cameraOn ? "block" : "hidden"}`}
                />
            </div>

            {/* Exam placeholder */}
            <div className="p-4 border rounded bg-gray-50">
                {testEnded ? (
                    <p className="text-red-600 font-bold">
                        ❌ Your exam has ended due to repeated security violations.
                    </p>
                ) : fullscreen && cameraOn ? (
                    faceDetected ? (
                        <p>✅ Secure exam running — Head: {headDirection}</p>
                    ) : (
                        <p>⚠️ Camera on but no face detected. Please stay in frame.</p>
                    )
                ) : (
                    <p>⚠️ Please enable camera and fullscreen to continue.</p>
                )}
            </div>

            {/* Logs */}
            <div className="p-4 border rounded bg-white max-h-48 overflow-y-auto text-sm">
                <h2 className="font-semibold">Session Logs</h2>
                <ul className="list-disc pl-5">
                    {logs.map((log, idx) => (
                        <li key={idx}>{log}</li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
